import { Component, OnInit, Input, OnDestroy, EventEmitter, Output, inject } from '@angular/core';
import { EntityTypeEnum, ITranslation } from 'src/app/classes/ITranslation';
import { WsmanagerService } from 'src/app/services/wsmanager.service';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { WidgetsmanagerService } from 'src/app/services/widgetsmanager.service';
import { TranslateModalErrorMapper } from 'src/app/classes/translateModalErrorMapper';
import { SettingsmanagerService } from 'src/app/services/settingsmanager.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-translation-modal',
  templateUrl: './translation-modal.component.html',
  styleUrls: ['./translation-modal.component.scss']
})
export class TranslationModalComponent implements OnInit, OnDestroy {
  @Input() entityId: number;
  @Input() entityType: EntityTypeEnum;
  @Input() parentSpinner: boolean | undefined;
  @Input() translations: ITranslation[] = [];
  @Input() isPopover = false;

  @Output() onBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() onRefreshContent: EventEmitter<{
    method: 'CREATE' | 'DELETE' | 'EDIT';
    translation: ITranslation;
  }> = new EventEmitter<{
    method: 'CREATE' | 'DELETE' | 'EDIT';
    translation: ITranslation;
  }>();

  public subscriptions: Subscription[] = [];
  public showNewTranslationForm = false;
  public editIndex = -1;
  private tempEdit: string | null = null;
  public localeOptions: { value: string; name: string }[] = [];
  public labelOptions: { value: string; name: string }[] = [];
  public showError = false;

  constructor(
    private plt: Platform,
    private wsManager: WsmanagerService,
    private widgetManager: WidgetsmanagerService,
    private settingsManager: SettingsmanagerService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.plt.ready().then(() => {
      this.localeOptions = this.settingsManager.getLocaleOptions();
      this.labelOptions = this.settingsManager.getLocalizedLabelOptionByEntity(this.entityType);
      if (typeof this.parentSpinner === 'undefined') {
        this.parentSpinner = false;
      }
    });
  }

  deleteTranslation(id: number) {
    this.subscriptions.push(
      this.wsManager.deleteTranslation(id).subscribe(
        (res) => {
          this.widgetManager.showToastMessage('success', this.translateService.instant('Translations.deleteSucceded'));
          const translation = res;
          translation.value = '';
          if (this.isPopover) {
            this.translations = this.translations.filter((t) => t.id !== translation.id);
          } else {
            this.onRefreshContent.emit({ method: 'DELETE', translation });
          }
        },
        (err) => {
          this.widgetManager.showToastMessage('error', this.translateService.instant('Translations.deleteFailed'));
        }
      )
    );
  }

  onTranslationCreated(translation: Pick<ITranslation, 'locale' | 'field' | 'value'>) {
    // Validazione entityId
    if (!this.entityId || this.entityId === -1) {
      this.widgetManager.showToastMessage('danger', this.translateService.instant('Translations.invalidEntity'), 5000);
      console.error('Cannot create translation: entityId is not valid', this.entityId);
      return;
    }

    // Log per debug
    console.log('Creating translation:', {
      entityType: this.entityType,
      entityId: this.entityId,
      translation
    });

    this.subscriptions.push(
      this.wsManager.createTranslationForEntity(this.entityType, this.entityId, translation).subscribe(
        (res) => {
          this.widgetManager.showToastMessage('success', this.translateService.instant('Translations.createSucceded'));
          if (this.isPopover) {
            this.translations = [...this.translations, res];
          } else {
            this.onRefreshContent.emit({
              method: 'CREATE',
              translation: res
            });
          }
          this.showNewTranslationForm = false;
        },
        ({ error }) => {
          console.error('Error creating translation:', error);
          console.error('Error message:', error.message);
          console.error('Error details:', JSON.stringify(error, null, 2));
          if (error.errors && Array.isArray(error.errors)) {
            console.error('Validation errors array:', error.errors);
            console.error('Number of validation errors:', error.errors.length);
            error.errors.forEach((err, index) => {
              console.error(`Validation Error ${index + 1}:`, err);
              console.error(`Error type: ${typeof err}`);
              console.error(`Error stringified:`, JSON.stringify(err, null, 2));
            });
          }
          const errorMessage = error.errors && error.errors.length > 0 
            ? error.errors.map(e => {
                if (typeof e === 'object') {
                  if (e.constraints) {
                    // Estrae i messaggi dalle constraints
                    const constraintMessages = Object.values(e.constraints).join(', ');
                    return `${e.field}: ${constraintMessages}`;
                  } else if (e.message) {
                    return e.message;
                  }
                } else if (typeof e === 'string') {
                  return e;
                }
                return JSON.stringify(e);
              }).join('; ')
            : TranslateModalErrorMapper.mapCreateErrorToMessage(error.message as string, this.translateService);
          console.error('Final error message to show:', errorMessage);
          this.widgetManager.showToastMessage('danger', errorMessage, 10000);
        }
      )
    );
  }

  goBack() {
    this.showNewTranslationForm = false;
    this.tempEdit = null;
    this.onBack.emit();
  }

  enableEdit(index: number) {
    if (this.editIndex === index) {
      this.editIndex = -1;
      this.tempEdit = null;
    } else {
      const t = this.translations[index];
      this.editIndex = index;
      this.tempEdit = t.value;
    }
  }
  saveEdits(i) {
    if (this.tempEdit === null || this.tempEdit.trim() === '') {
      this.showError = true;
      return;
    }
    if (this.tempEdit === this.translations[i].value) {
      this.enableEdit(i);
    }
    this.wsManager.editTranslation(this.translations[i].id, this.tempEdit).subscribe(
      (res) => {
        this.widgetManager.showToastMessage('success', this.translateService.instant('Translations.editSucceded'));
        if (this.isPopover) {
          this.translations[i] = res;
        } else {
          this.onRefreshContent.emit({ method: 'EDIT', translation: res });
        }
        this.editIndex = -1;
        this.tempEdit = null;
      },
      ({ error }) => {
        this.widgetManager.showToastMessage('danger', this.translateService.instant('Translations.editFailed'));
      }
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getDisplayTranslationValue(value: string): string {
    if (!value) {
      return '';
    }
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
